import { TrendingUp, Users, Award, X, Sparkles, BellOff } from "lucide-react";
import { Button } from "../ui/Button";
import { Card } from "../ui/Card";
import { IconBox } from "../ui/IconBox";
import BgImg from "../../assets/images/BeAshark.webp";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
const Hero = () => {
  const [showModal, setShowModal] = useState(false);
  const [showDontShowConfirm, setShowDontShowConfirm] = useState(false);
  const navigate = useNavigate();

  const GOOGLE_FORM_URL = "https://docs.google.com/forms/d/e/1FAIpQLSeZtTaG_Ln6ekHOW8OsqZ5l8F4sppXBohxmew24cAusQOFkHA/viewform";

  const features = [
    {
      icon: TrendingUp,
      title: "Funding",
      description: "Connect with angels & VCs",
    },
    {
      icon: Users,
      title: "1:1 Meetings",
      description: "Private investor sessions",
    },
    {
      icon: Award,
      title: "Pitch Day",
      description: "Pitch on stage + feedback",
    },
  ];

  useEffect(() => {
    // Check if user has chosen "Don't show again"
    const dontShowAgain = localStorage.getItem('beashark_dont_show_modal');
    
    if (dontShowAgain !== 'true') {
      // Show modal after 3 seconds on every visit
      const timer = setTimeout(() => {
        setShowModal(true);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, []);

  const handleStartupClick = () => {
    setShowModal(false);
    navigate("/");
  };

  const handleInvestorClick = () => {
    setShowModal(false);
    navigate("/investor");
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleDontShowAgain = () => {
    setShowDontShowConfirm(true);
  };

  const confirmDontShowAgain = () => {
    localStorage.setItem('beashark_dont_show_modal', 'true');
    setShowModal(false);
    setShowDontShowConfirm(false);
  };

  const cancelDontShowAgain = () => {
    setShowDontShowConfirm(false);
  };

  // Function to reset the "Don't show again" preference
  const resetModalPreference = () => {
    localStorage.removeItem('beashark_dont_show_modal');
    alert("Modal preferences have been reset. The modal will appear again on your next visit.");
  };

  // Function to open Google Form in new tab
  const openGoogleForm = () => {
    window.open(GOOGLE_FORM_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <section className="relative min-h-[90vh] flex items-center pt-20 pb-6 px-6 bg-primary text-textPrimary overflow-hidden scroll-mt-24 lg:mt-10">
        {/* Optional: Add a reset button for testing (remove in production) */}
        {process.env.NODE_ENV === 'development' && (
          <button 
            onClick={resetModalPreference}
            className="absolute top-4 right-4 z-20 text-xs bg-gray-800 text-white px-2 py-1 rounded opacity-50 hover:opacity-100"
            title="Reset modal preferences (dev only)"
          >
            Reset Modal
          </button>
        )}

        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src={BgImg} 
            alt="BeAshark Background" 
            className="absolute inset-0 w-full h-full object-cover" 
          />
          <div className="absolute inset-0 bg-black/40"></div>
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto py-20">
          {/* Hero content with event info on right */}
          <div className="flex flex-col lg:flex-row items-start gap-8 lg:gap-16 -mt-8">
            {/* Left side - Main content */}
            <div className="flex-1 max-w-2xl lg:max-w-3xl ">
              {/* Mobile: Adjusted typography, Desktop: Same as before */}
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight space-y-2">
                <span className="block animate-fadeUp">Pitch Your Startup.</span>
                <span className="block animate-fadeUp delay-150">
                  Meet the Investors.
                </span>
                <span className="block animate-fadeUp delay-300 text-bluePrimary">
                  Get Funded.
                </span>
              </h1>

              <p className="text-lg md:text-xl text-textMuted mb-8 leading-relaxed">
                Join AQT — a founder-first pitch event that connects
                ambitious startups with active investors, mentors and media
                exposure.
              </p>

              {/* NOMINATE NOW BUTTON - Mobile: full width, Desktop: auto width */}
              <div className="flex justify-start">
                <Button
                  onClick={openGoogleForm}
                  className="bg-gradient-to-r from-bluePrimary to-blueHover hover:from-blueHover hover:to-blue-800 
                            text-white px-8 md:px-10 py-4 rounded-xl shadow-lg hover:shadow-xl 
                            transition-all duration-300 transform hover:-translate-y-1 
                            text-base md:text-lg font-semibold w-full md:w-auto"
                  size="lg"
                >
                  Nominate Now
                </Button>
              </div>
            </div>

            {/* Right side - Event info - Mobile: below, Desktop: right side */}
            <div className="w-full lg:w-72 lg:ml-auto -mt-6 sm:mt-0">

              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-4 md:p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-bluePrimary/20 flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 md:w-5 md:h-5 text-bluePrimary" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="min-w-0">
                    <div className="text-goldPrimary font-semibold text-xs md:text-sm uppercase tracking-wider truncate">Event Date</div>
                    <div className="text-white font-bold text-base md:text-lg truncate">31 January 2026</div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-goldPrimary/20 flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 md:w-5 md:h-5 text-goldPrimary" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="min-w-0">
  <div className="text-bluePrimary font-semibold text-xs md:text-sm uppercase tracking-wider truncate">Venue</div>
  <div className="text-white font-bold text-base md:text-lg">
    Holiday Inn,<br />Gurugram
  </div>
</div>
                </div>

                {/* Decorative line
                <div className="mt-4 md:mt-6 pt-4 md:pt-6 border-t border-white/20">
                  <p className="text-textMuted text-xs text-center">
                    Details will be announced shortly
                  </p>
                </div> */}
              </div>
            </div>
          </div>

          {/* FEATURE CARDS - Mobile: 1 column, Desktop: 3 columns */}
          <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 max-w-5xl">
            {features.map((feature, index) => (
              <Card
                key={index}
                className="p-4 md:p-6 rounded-xl md:rounded-2xl bg-tertiary border border-borderLight shadow-card 
                         hover:shadow-xl transition-all hover:bg-secondary/60 group"
                hover
              >
                <div className="flex items-start gap-3">
                  <IconBox
                    icon={feature.icon}
                    size="sm"
                    className="mb-0 rounded-lg md:rounded-xl bg-blueTint text-bluePrimary 
                             group-hover:text-white transition-colors flex-shrink-0"
                  />
                  <div className="min-w-0">
                    <h3 className="text-base md:text-lg font-bold text-black mb-1 
                                 group-hover:text-white transition-colors truncate">
                      {feature.title}
                    </h3>
                    <p className="text-black text-xs md:text-sm
                                group-hover:text-white transition-colors truncate">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Auto-show Modal - Fixed with higher z-index */}
      {showModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="relative bg-gradient-to-br from-tertiary via-primary to-secondary rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-fadeIn border border-borderLight">
            {/* Gold glow effect */}
            <div className="absolute inset-0 rounded-2xl shadow-gold-glow pointer-events-none"></div>
            
            {/* Close button */}
            <button
              onClick={handleCloseModal}
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-tertiary hover:bg-secondary transition-colors border border-borderLight"
              aria-label="Close modal"
            >
              <X className="w-5 h-5 text-textMuted hover:text-textPrimary" />
            </button>

            {/* Modal Content */}
            <div className="p-8">
              <div className="text-center mb-6">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-bluePrimary to-blueHover flex items-center justify-center shadow-lg">
                  <Sparkles className="w-10 h-10 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-textPrimary mb-2">
                  Ready to Make Waves?
                </h2>
                <p className="text-textMuted">
                  Are you building the future or investing in it? Choose your path below.
                </p>
              </div>

              <div className="space-y-4">
                {/* Startups Button */}
                <button
                  onClick={handleStartupClick}
                  className="w-full p-4 rounded-xl bg-gradient-to-r from-bluePrimary to-blueHover text-white font-semibold text-lg
                           hover:from-blueHover hover:to-blue-800 transition-all duration-300 transform hover:-translate-y-1
                           shadow-lg hover:shadow-xl flex items-center justify-center gap-3 group"
                >
                  <Award className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  <span className="group-hover:tracking-wide transition-all">
                    I'm a Startup Founder
                  </span>
                </button>

                {/* Investors Button */}
                <button
                  onClick={handleInvestorClick}
                  className="w-full p-4 rounded-xl bg-gradient-to-r from-gray-800 to-gray-900 text-white font-semibold text-lg
                           hover:from-gray-900 hover:to-black transition-all duration-300 transform hover:-translate-y-1
                           shadow-lg hover:shadow-xl flex items-center justify-center gap-3 group border border-gray-700"
                >
                  <Users className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  <span className="group-hover:tracking-wide transition-all">
                    I'm an Investor
                  </span>
                </button>
              </div>

              {/* "Don't show again" option */}
              <div className="mt-6 flex items-center justify-center">
                <button
                  onClick={handleDontShowAgain}
                  className="text-sm text-gray-500 hover:text-gray-700 transition-colors flex items-center gap-2"
                >
                  <BellOff className="w-4 h-4" />
                  Don't show this message again
                </button>
              </div>

              <div className="mt-8 pt-6 border-t border-borderLight">
                <p className="text-center text-textMuted text-sm">
                  <span className="text-goldPrimary font-semibold">21+</span> startups and{" "}
                  <span className="text-bluePrimary font-semibold">117+</span> investors already on board
                </p>
              </div>
            </div>

            {/* Bottom decorative gradient */}
            <div className="h-1 bg-gradient-to-r from-bluePrimary via-goldPrimary to-bluePrimary"></div>
          </div>
        </div>
      )}

      {/* Confirmation Modal for "Don't show again" */}
      {showDontShowConfirm && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm">
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden animate-fadeIn">
            <div className="p-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shadow-lg">
                  <BellOff className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Don't show this again?
                </h3>
                <p className="text-gray-600 text-sm">
                  This message won't appear on future visits. You can change this preference in your browser settings.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={cancelDontShowAgain}
                  className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDontShowAgain}
                  className="flex-1 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 text-white hover:from-amber-600 hover:to-amber-700 transition-all font-medium"
                >
                  Don't Show Again
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Hero;